import {
  Component,
  effect,
  input,
  Injector,
  OnDestroy,
  OnInit,
  output,
  runInInjectionContext,
  inject,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatRadioChange, MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatSelectChange, MatOption } from '@angular/material/select';

import { DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';

import { map, Observable, startWith, Subject, takeUntil } from 'rxjs';
import { PotatoDiseases, QuestionnaireAnswer } from 'src/app/tga/shared/models/models';

import { DiseaseExplainDialogComponent } from '../../../dialogs/disease-explain-dialog/disease-explain-dialog.component';
import {
  TGACheckboxQuestion,
  TGADateYearQuestion,
  TgaInputType,
  TGANumberQuestion,
  TGAQuestion,
  TGATableQuestion,
  TGAWarningMessageQuestion,
} from '../../models/question-models';
import { FormComponent, AgrNumberDirective } from '@next/core-lib/form';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { AlertComponent } from '../../../alert/alert.component';
import { MatFormField, MatLabel, MatError, MatSuffix } from '@angular/material/form-field';
import { NgClass, AsyncPipe } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { SpecificSoilQuestionnaireService } from '../specific/soil-questionnaire/specific-soil-questionnaire.service';

// Custom date formats for year-only display
export const YEAR_FORMATS = {
  parse: {
    dateInput: 'YYYY',
  },
  display: {
    dateInput: 'YYYY',
    monthYearLabel: 'YYYY',
    dateA11yLabel: 'YYYY',
    monthYearA11yLabel: 'YYYY',
  },
};

@Component({
  selector: 'app-question-form',
  templateUrl: './question-form.component.html',
  styleUrls: ['./question-form.component.scss'],
  providers: [{ provide: MAT_DATE_FORMATS, useValue: YEAR_FORMATS }],
  imports: [
    FormComponent,
    FormsModule,
    ReactiveFormsModule,
    MatCheckbox,
    MatIconButton,
    MatIcon,
    MatTooltip,
    AlertComponent,
    MatFormField,
    NgClass,
    MatLabel,
    MatInput,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    MatRadioGroup,
    MatRadioButton,
    AgrNumberDirective,
    MatError,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class QuestionFormComponent implements OnInit, OnDestroy {
  private readonly locale = inject(LocaleService);
  private readonly dialog = inject(DialogService);
  private readonly injector = inject(Injector);

  question = input.required<TGAQuestion<TgaInputType>>();
  formGroup = input.required<FormGroup>();
  readOnly = input(false);
  valueChanged = output<QuestionnaireAnswer>();

  filteredOptions$: Observable<{ key: string; value: string }[]> = new Observable();
  autocompleteControl = new FormControl('');
  private readonly destroy$ = new Subject<void>();

  constructor() { }

  ngOnInit(): void {
    // Inputs are guaranteed to be set from here, avoid reading required inputs in constructor.
    runInInjectionContext(this.injector, () => {
      effect(() => {
        const question = this.question();
        if (question.controlType === 'dropdown') {
          const formGroup = this.formGroup();
          const formControl = formGroup.controls[question.key] as FormControl;
          const currentValue = formControl?.value;

          if (currentValue) {
            const displayValue = this.getDisplayValue(currentValue);
            if (this.autocompleteControl.value !== displayValue) {
              this.autocompleteControl.setValue(displayValue, { emitEvent: false });
            }
          } else if (this.autocompleteControl.value) {
            this.autocompleteControl.setValue('', { emitEvent: false });
          }

          if ((this.readOnly() || formControl?.disabled) && !this.autocompleteControl.disabled) {
            this.autocompleteControl.disable({ emitEvent: false });
          } else if (!this.readOnly() && formControl?.enabled && this.autocompleteControl.disabled) {
            this.autocompleteControl.enable({ emitEvent: false });
          }
        }
      });
    });

    this.locale.localeDataChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.question().controlType === 'dropdown' && this.autocompleteControl.value) {
        const currentValue = this.autocompleteControl.value;
        this.autocompleteControl.patchValue(currentValue, { emitEvent: false });
      }
    });

    if (this.question().controlType === 'dropdown') {
      const shouldFilter = this.question().key !== SpecificSoilQuestionnaireService.hadGrasslandOrGrassSeed;

      this.filteredOptions$ = this.autocompleteControl.valueChanges.pipe(
        startWith(this.autocompleteControl.value || ''),
        map((value: string | null) =>
          shouldFilter ? this.filterOptions(value || '', this.question().options || []) : this.question().options || [],
        ),
      );
    }
  }

  private getDisplayValue(key: string | number): string {
    const selectedOption = this.question().options?.find((opt) => opt.key === key.toString());
    return selectedOption?.value || '';
  }

  displayTranslatedValue = (value: string): string => {
    return this.locale.translate(value);
  };

  private filterOptions(value: string, items: { key: string; value: string }[]) {
    const filterValueLower = this.locale.translate(value).toLowerCase();

    return [...items]
      .sort((a, b) => {
        const aTranslated = this.locale.translate(a.value);
        const bTranslated = this.locale.translate(b.value);
        return aTranslated.toLowerCase().localeCompare(bTranslated.toLowerCase());
      })
      .filter((option) => {
        const translatedValue = this.locale.translate(option.value);
        return translatedValue.toLowerCase().includes(filterValueLower);
      });
  }

  getFormControl(key: string): FormControl {
    return this.formGroup().controls[key] as FormControl;
  }

  getRequiredTag(): string {
    return this.question().required ? ' ' : '';
  }

  isInvalid(): boolean {
    const control = this.getFormControl(this.question().key);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  isInvalidRequired(): boolean {
    const question = this.question();
    const control = this.getFormControl(question.key);
    const formGroup = this.formGroup() as FormGroup & { __showValidation?: boolean };
    const showValidation = formGroup.__showValidation === true;
    return !!(showValidation && question.required && control && control.invalid && (control.dirty || control.touched));
  }

  asDateYearQuestion(): TGADateYearQuestion {
    return this.question() as TGADateYearQuestion;
  }

  asWarningMessageQuestion(): TGAWarningMessageQuestion {
    return this.question() as TGAWarningMessageQuestion;
  }

  asTableQuestion(): TGATableQuestion {
    return this.question() as TGATableQuestion;
  }

  asNumberQuestion(): TGANumberQuestion {
    return this.question() as TGANumberQuestion;
  }

  asCheckBoxQuestion(): TGACheckboxQuestion {
    return this.question() as TGACheckboxQuestion;
  }

  isWarningMessageQuestion(question: TGAQuestion<TgaInputType>): boolean {
    return question.controlType === 'warning-message';
  }

  onCheckBoxChange() {
    if (this.readOnly()) return;

    const key = this.question().key;
    const question = this.question();
    if (!question.options) return;

    const checkboxValues = question.value as { key: string; selected: boolean }[];
    if (!Array.isArray(checkboxValues)) return;

    const selectedOptions = question.options
      .filter((_, index) => index < checkboxValues.length && checkboxValues[index]?.selected)
      .map((option) => option.key);

    this.updateFormValue(key, selectedOptions);
    this.valueChanged.emit({ key, value: selectedOptions });
  }

  updateRadioOrDropDownValue(newValue: MatRadioChange | MatSelectChange | MatAutocompleteSelectedEvent) {
    if (this.readOnly()) return;

    const key = this.question().key;
    let value: string | number;

    if ((newValue as MatAutocompleteSelectedEvent).option) {
      const autocompleteEvent = newValue as MatAutocompleteSelectedEvent;
      const selectedDisplayValue = autocompleteEvent.option.value;
      const selectedOption = this.question().options?.find((opt) => opt.value === selectedDisplayValue);
      value = selectedOption?.key || selectedDisplayValue;
    } else {
      value = (newValue as MatRadioChange | MatSelectChange).value;
    }

    this.updateFormValue(key, value);
    this.valueChanged.emit({ key, value });

    if (this.question().controlType === 'dropdown') {
      const displayValue = this.getDisplayValue(value);
      this.autocompleteControl.setValue(displayValue, { emitEvent: false });
    }
  }

  updateNumberValue(event: Event) {
    if (this.readOnly()) return;

    const key = this.question().key;
    const numberQuestion = this.asNumberQuestion();

    const inputElement = event.target as HTMLInputElement;
    const inputValue = inputElement.value.trim();
    const value = inputValue === '' ? undefined : Number(inputValue);

    // Validate that the value is a valid number (not NaN)
    const formControl = this.getFormControl(key);
    if (value !== undefined && Number.isNaN(value)) {
      formControl.setErrors({
        invalidNumber: {
          actualValue: inputValue,
          message: this.locale.translate('TGA.Questionnaire.PleaseEnterValidNumber'),
        },
      });
      return; // Don't emit value change if validation fails
    }

    // Validate range if the question has range constraints
    if (value !== undefined && numberQuestion.range) {
      const { min, max } = numberQuestion.range;
      if (value < min || value > max) {
        formControl.setErrors({
          range: {
            actualValue: value,
            min,
            max,
            message: this.locale.translate('TGA.Questionnaire.NumberValidationMessage', {
              min: min.toString(),
              max: max.toString(),
            }),
          },
        });
        return; // Don't emit value change if validation fails
      }
    }

    // Clear any existing validation errors if validation passes
    if (formControl.hasError('range') || formControl.hasError('invalidNumber')) {
      const errors = { ...formControl.errors };
      delete errors['range'];
      delete errors['invalidNumber'];
      const newErrors = Object.keys(errors).length > 0 ? errors : null;

      // Update form value manually to preserve other validation errors
      formControl.setValue(value, { emitEvent: true, onlySelf: false });
      formControl.setErrors(newErrors);
      formControl.markAsDirty();
      formControl.markAsTouched();
    } else {
      // No validation errors to preserve, use the normal update method
      this.updateFormValue(key, value);
    }

    this.valueChanged.emit({ key, value });
  }

  updateTextValue(event: Event) {
    if (this.readOnly()) return;

    const key = this.question().key;

    const inputElement = event.target as HTMLInputElement;
    const value = inputElement.value;

    this.updateFormValue(key, value);
    this.valueChanged.emit({ key, value });
  }

  private updateFormValue(key: string, value: string | number | boolean | string[] | undefined): void {
    const formGroup = this.formGroup();

    const control = formGroup.get(key);
    if (!control) return;

    control.setValue(value, {
      emitEvent: true,
      onlySelf: false,
    });

    control.markAsDirty();
    control.markAsTouched();

    formGroup.updateValueAndValidity({ emitEvent: true });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public onYearSelected(key: string, date: any, datepicker: MatDatepicker<Date>) {
    if (this.readOnly()) return;

    const control = this.formGroup().get(key);
    if (!control) return;

    const nativeDate: Date | null = date instanceof Date ? date : (date?._d ?? null);
    if (!nativeDate || Number.isNaN(nativeDate.getTime())) {
      control.setValue(null);
      this.valueChanged.emit({ key, value: undefined });
      datepicker.close();
      return;
    }

    const normalizedYear = nativeDate.getFullYear();
    const yearDate = new Date(Date.UTC(normalizedYear, 0, 1));
    control.setValue(yearDate);
    this.valueChanged.emit({ key, value: normalizedYear });
    datepicker.close();
  }

  onIconClick(option: { value: string }): void {
    let imagePath: string;
    let translationKey: string;

    switch (option.value) {
      case PotatoDiseases.Rhizoctonia:
        imagePath = 'assets/images/rhizoctonia-disease.jpg';
        translationKey = 'Diseases.rhizoctonia';
        break;
      case PotatoDiseases.Zilverschurft:
        imagePath = 'assets/images/zilverschurft-disease.jpg';
        translationKey = 'Diseases.zilverschurft';
        break;
      case PotatoDiseases.Fusarium:
        imagePath = 'assets/images/fusarium-disease.jpg';
        translationKey = 'Diseases.fusarium';
        break;
      default:
        return;
    }

    this.openDiseaseDialog(imagePath, translationKey);
  }

  openDiseaseDialog(imagePath: string, translationKey: string): void {
    const diseaseName = this.locale.translate(translationKey);
    this.dialog.responsiveDialog(DiseaseExplainDialogComponent, {
      imagePath,
      diseaseName,
      title: diseaseName,
      cancelButton: false,
      disableClose: true,
      confirmButtonText: this.locale.translate('close'),
      confirmButton: true,
      cancelButtonText: this.locale.translate('cancel'),
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
